import 'intersection-observer'; // IntersectionObserver polyfill
import React, { RefObject, useEffect, useRef } from 'react';

export type InviewEventHandler = (
  change: IntersectionObserverEntry,
  observer: IntersectionObserver
) => void;

interface InviewProps<T extends HTMLElement> {
  ref: React.RefObject<T | null | undefined>;
  offset?: string;
  once?: boolean;
  onInview?: InviewEventHandler;
  onOutview?: InviewEventHandler;
  enabled?: boolean;
}

export const useInview = <T extends HTMLElement>({
  ref,
  offset = '0px',
  once,
  onInview = undefined,
  onOutview = undefined,
}: InviewProps<T>) => {
  useEffect(() => {
    // Early return if ref is not available or IntersectionObserver is not supported
    if (
      !ref.current ||
      typeof window === 'undefined' ||
      !window.IntersectionObserver
    ) {
      return;
    }

    const element = ref.current;
    const observer = new IntersectionObserver(
      (changes, observer) => {
        const change = changes[0];

        if (change.intersectionRatio === 1) {
          if (once) observer.unobserve(change.target);

          return ref.current && onInview?.(change, observer);
        }

        if (change.intersectionRatio !== 1) {
          return ref.current && onOutview?.(change, observer);
        }
      },
      {
        rootMargin: offset,
        threshold: 1.0,
      }
    );

    observer.observe(element);

    return () => {
      if (element && observer) {
        observer.unobserve(element);
      }
      if (observer) {
        observer.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

interface InviewForProps {
  ref: RefObject<HTMLElement>;
  once?: boolean;
  timeout: number;
  cb: () => void;
}

export const useInviewFor = ({ ref, once, timeout, cb }: InviewForProps) => {
  const callback = useRef(cb);
  callback.current = cb;
  useEffect(() => {
    let viewTimeout: NodeJS.Timeout | null;

    // Early return if ref is not available or IntersectionObserver is not supported
    if (
      !ref.current ||
      typeof window === 'undefined' ||
      !window.IntersectionObserver
    ) {
      return;
    }

    const element = ref.current;

    function onIn() {
      if (!viewTimeout) {
        viewTimeout = setTimeout(() => {
          if (element) {
            callback.current();
            viewTimeout = null;
            if (once && observer) observer.unobserve(element);
          }
        }, timeout);
      }
    }

    function onOut() {
      if (viewTimeout) {
        clearTimeout(viewTimeout);
        viewTimeout = null;
      }
    }

    const observer = new IntersectionObserver(
      (changes) => {
        const change = changes[0];
        if (change.intersectionRatio === 1) {
          return onIn();
        } else {
          onOut();
        }
      },
      {
        threshold: 1.0,
      }
    );

    observer.observe(element);

    return function cleanup() {
      if (viewTimeout) {
        clearTimeout(viewTimeout);
        viewTimeout = null;
      }
      if (element && observer) {
        observer.unobserve(element);
      }
      if (observer) {
        observer.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current]);
};

export default useInview;
